import React, { useEffect, useState } from 'react';

import {
    Button,
    Checkbox,
    Divider,
    FormControl,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    TableCell,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';
import ViewKanbanRoundedIcon from '@mui/icons-material/ViewKanbanRounded';

import DayRangePicker from '../../components/dayRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import { FASE_OPTIONS, LEAD_COLUMNS, ORIGEM_OPTIONS, VENDEDOR_OPTIONS, buildGroupedStatusOptions } from './crmShared';
import {
    CRM_LEAD_DATE_FILTER_RANGES,
    CRM_LEAD_KANBAN_BAR_DATE_FIELD,
    CRM_LEADS_VIEW_KANBAN,
    CRM_LEADS_VIEW_TABLE,
    CRM_LEAD_INTEGER_FILTER_FIELDS,
    CRM_LEAD_MARKETING_FIELDS,
    CRM_LEAD_REQUIRED_COLUMN_FIELD,
    CRM_LEAD_TEXT_FILTER_FIELDS,
    getCrmLeadColumnFilterField,
    toIdArray
} from './crmLeadsData';

const selectConfig = {
    vendedor_id: { allLabel: 'Todos', options: VENDEDOR_OPTIONS },
    fase_id: { allLabel: 'Todas', options: FASE_OPTIONS },
    origem_id: { allLabel: 'Todas', options: ORIGEM_OPTIONS },
    ordem_id: {
        options: [
            { id: 0, label: 'Nenhum' },
            { id: 1, label: 'Descendente' },
            { id: 2, label: 'Ascendente' }
        ]
    }
};

const allMultiSelectOption = 'all';

const multiSelectConfig = {
    status_id: [
        { value: allMultiSelectOption, label: 'Todos' },
        ...buildGroupedStatusOptions()
    ]
};

const selectMenuProps = {
    PaperProps: { className: 'crm-dashboard-select-menu-paper' },
    MenuListProps: { className: 'crm-dashboard-select-menu-list' }
};

const renderSelectFilter = (field, filters, onFilterChange, ariaLabel) => {
    const config = selectConfig[field];

    return (
        <FormControl size='small' className='crm-leads-column-filter-control'>
            <Select
                aria-label={`Filtrar por ${ariaLabel}`}
                value={filters[field]}
                onChange={(e) => onFilterChange(field, e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={selectMenuProps}
            >
                {config.allLabel && <MenuItem className='crm-dashboard-select-menu-item' value=''>{config.allLabel}</MenuItem>}
                {config.options.map((option) => (
                    <MenuItem className='crm-dashboard-select-menu-item' key={option.id} value={option.id}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const areSameIdLists = (currentIds, nextIds) => (
    currentIds.length === nextIds.length &&
    currentIds.every((id, index) => id === nextIds[index])
);

const MultiSelectColumnFilter = ({ ariaLabel, field, filters, onFilterChange }) => {
    const appliedValues = toIdArray(filters[field]);
    const [draftValues, setDraftValues] = useState(appliedValues);

    useEffect(() => {
        setDraftValues(toIdArray(filters[field]));
    }, [field, filters]);

    const handleClose = () => {
        if (areSameIdLists(appliedValues, draftValues)) return;
        onFilterChange(field, draftValues);
    };

    return (
        <MultiSelectFilter
            className='crm-leads-column-filter-control'
            size='small'
            ariaLabel={`Filtrar por ${ariaLabel}`}
            value={draftValues}
            options={multiSelectConfig[field]}
            allOptionValue={allMultiSelectOption}
            onChange={(values) => setDraftValues(values.includes(allMultiSelectOption) ? [] : values)}
            onClose={handleClose}
        />
    );
};

const DateRangeFilter = ({ column, dateFilters, onDateRangeApply }) => {
    const range = CRM_LEAD_DATE_FILTER_RANGES[column.field];
    const appliedStartDate = dateFilters[range.start];
    const appliedEndDate = dateFilters[range.end];

    return (
        <DayRangePicker
            ariaLabel={`Filtrar ${column.headerName} por período`}
            startDate={appliedStartDate}
            endDate={appliedEndDate}
            maxDate={new Date()}
            onApply={(startDate, endDate) => onDateRangeApply(range, startDate, endDate)}
            onClear={() => onDateRangeApply(range, null, null)}
        />
    );
};

const renderColumnFilter = ({
    canViewMarketingFilters,
    column,
    dateFilters,
    filters,
    onDateRangeApply,
    onFilterChange
}) => {
    if (CRM_LEAD_MARKETING_FIELDS.includes(column.field) && !canViewMarketingFilters) return null;

    const filterField = getCrmLeadColumnFilterField(column.field);

    if (CRM_LEAD_DATE_FILTER_RANGES[column.field]) {
        return (
            <DateRangeFilter
                column={column}
                dateFilters={dateFilters}
                onDateRangeApply={onDateRangeApply}
            />
        );
    }

    if (multiSelectConfig[filterField]) {
        return (
            <MultiSelectColumnFilter
                ariaLabel={column.headerName}
                field={filterField}
                filters={filters}
                onFilterChange={onFilterChange}
            />
        );
    }

    if (selectConfig[filterField]) {
        return renderSelectFilter(filterField, filters, onFilterChange, column.headerName);
    }

    if (CRM_LEAD_TEXT_FILTER_FIELDS.includes(filterField) || CRM_LEAD_INTEGER_FILTER_FIELDS.includes(filterField)) {
        return (
            <TextField
                size='small'
                type={CRM_LEAD_INTEGER_FILTER_FIELDS.includes(filterField) ? 'number' : 'text'}
                value={filters[filterField] ?? ''}
                onChange={(event) => onFilterChange(filterField, event.target.value)}
                placeholder='Filtrar'
                inputProps={{ 'aria-label': `Filtrar por ${column.headerName}` }}
                fullWidth
                className='crm-leads-column-text-filter'
            />
        );
    }

    return null;
};

export const CrmLeadsFilters = ({
    filters,
    onFilterChange,
    dateFilters,
    onDateRangeApply,
    canViewMarketingFilters,
    columns,
    getStickyProps = () => ({ className: '', style: undefined })
}) => {
    return (
        <TableRow className='crm-leads-filter-row'>
            {columns.map((column) => (
                <TableCell
                    key={column.field}
                    className={`crm-leads-filter-cell ${getStickyProps(column.field).className}`}
                    data-field={column.field}
                    style={getStickyProps(column.field).style}
                >
                    {renderColumnFilter({
                        canViewMarketingFilters,
                        column,
                        dateFilters,
                        filters,
                        onDateRangeApply,
                        onFilterChange
                    })}
                </TableCell>
            ))}
        </TableRow>
    );
};

export const CrmLeadsColumnPicker = ({ columns, visibleFields, onToggleColumn, onRestoreDefault }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    return (
        <>
            <Button
                size='small'
                variant='outlined'
                startIcon={<ViewColumnRoundedIcon />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-haspopup='menu'
                aria-expanded={Boolean(anchorEl)}
                aria-controls={anchorEl ? 'crm-leads-column-menu' : undefined}
            >
                Colunas
            </Button>
            <Menu
                id='crm-leads-column-menu'
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ className: 'crm-dashboard-select-menu-paper crm-leads-column-menu-paper' }}
                MenuListProps={{ className: 'crm-dashboard-select-menu-list', dense: true }}
            >
                {columns.map((column) => {
                    const isRequired = column.field === CRM_LEAD_REQUIRED_COLUMN_FIELD;
                    const isVisible = isRequired || visibleFields.includes(column.field);

                    return (
                        <MenuItem
                            key={column.field}
                            className='crm-dashboard-select-menu-item crm-leads-column-menu-item'
                            disabled={isRequired}
                            onClick={() => onToggleColumn(column.field)}
                        >
                            <Checkbox size='small' checked={isVisible} disableRipple tabIndex={-1} />
                            <ListItemText primary={column.headerName} />
                        </MenuItem>
                    );
                })}
                <Divider />
                <MenuItem className='crm-dashboard-select-menu-item crm-leads-column-menu-item' onClick={onRestoreDefault}>
                    <ListItemText primary='Restaurar padrão' />
                </MenuItem>
            </Menu>
        </>
    );
};

const kanbanBarColumns = ['vendedor', 'status', 'origem', 'telefone', CRM_LEAD_KANBAN_BAR_DATE_FIELD]
    .map((field) => LEAD_COLUMNS.find((column) => column.field === field));

export const CrmLeadsKanbanFilters = ({
    filters,
    onFilterChange,
    dateFilters,
    onDateRangeApply,
    tableFilterCount,
    onClearTableFilters
}) => (
    <div className='crm-leads-kanban-filters'>
        {kanbanBarColumns.map((column) => (
            <div key={column.field} className='crm-leads-kanban-filter' data-field={column.field}>
                <span className='crm-leads-kanban-filter-label'>{column.headerName}</span>
                {renderColumnFilter({
                    canViewMarketingFilters: false,
                    column,
                    dateFilters,
                    filters,
                    onDateRangeApply,
                    onFilterChange
                })}
            </div>
        ))}

        {tableFilterCount > 0 && (
            <button type='button' className='crm-leads-kanban-table-filters-chip' onClick={onClearTableFilters}>
                <span>
                    {tableFilterCount} {tableFilterCount === 1 ? 'filtro da tabela ativo' : 'filtros da tabela ativos'} · limpar
                </span>
                <CloseRoundedIcon fontSize='inherit' />
            </button>
        )}
    </div>
);

export const CrmLeadsViewToggle = ({ value, onChange }) => (
    <ToggleButtonGroup
        exclusive
        size='small'
        value={value}
        onChange={(event, nextValue) => nextValue && onChange(nextValue)}
        className='crm-leads-view-toggle'
        aria-label='Visualização dos leads'
    >
        <ToggleButton value={CRM_LEADS_VIEW_TABLE} className='crm-leads-view-toggle-button'>
            <TableRowsRoundedIcon fontSize='inherit' />
            <span>Tabela</span>
        </ToggleButton>
        <ToggleButton value={CRM_LEADS_VIEW_KANBAN} className='crm-leads-view-toggle-button'>
            <ViewKanbanRoundedIcon fontSize='inherit' />
            <span>Funil</span>
        </ToggleButton>
    </ToggleButtonGroup>
);
